(function () {
  'use strict';

  const module = angular.module('client.directives');

  module.directive('quizTextToSpeech', quizTextToSpeechDirective);

  function quizTextToSpeechDirective() {
    return {
      restrict: 'E',
      scope: {
        elementId: '<',
        getQuestionElement: '&'
      },
      template: '<div class="text-right"><react-component component="TextToSpeech" props="ctrl.getTextToSpeechProps()"></react-component></div>',
      controller: quizTextToSpeechController,
      controllerAs: 'ctrl',
      bindToController: true
    };
  }

  quizTextToSpeechController.$inject = ['$window', '$log'];

  function quizTextToSpeechController($window, $log) {
    const ctrl = this;

    ctrl.textToSpeechProps = { items: [], getPlaybackItems: getPlaybackItems, onItemPlay: handleItemPlay, onEnd: clearHighlight };

    const dragDropImageCache = {};
    const htmlParser = new DOMParser();
    let highlightedElement = null;

    ctrl.$onChanges = function(changes) {
      if (changes.elementId) {
        clearHighlight();
      }
    };
    ctrl.$onDestroy = clearHighlight;

    function sanitizeTextForSpeech(value) {
      if (value === null || value === undefined) {
        return '';
      }
      const rawText = String(value);
      const decodedText = htmlParser.parseFromString(rawText, 'text/html').body.textContent || '';
      const plainText = htmlParser.parseFromString(decodedText, 'text/html').body.textContent || '';
      return plainText.replace(/\s+/g, ' ').trim();
    }

    function pushSanitizedItem(items, id, text) {
      items.push({ id: id, text: sanitizeTextForSpeech(text) });
    }

    function clearHighlight() {
      if (highlightedElement) {
        highlightedElement.classList.remove('tts-highlight');
        highlightedElement = null;
      }
    }

    function findAndHighlightText(rootElement, itemId) {
      if (!rootElement || !itemId) {
        return false;
      }
      let targetElement = rootElement.querySelector('#' + CSS.escape(itemId));
      if (!targetElement) {
        targetElement = document.getElementById(itemId);
      }
      if (!targetElement) {
        return false;
      }
      if (!targetElement.textContent) {
        return false;
      }
      // Playback highlighting must not overwrite the student's next selection.
      targetElement.classList.add('tts-highlight');
      highlightedElement = targetElement;
      return true;
    }

    function handleItemPlay(itemId) {
      clearHighlight();
      const elementContainer = document.getElementById(ctrl.elementId);
      if (elementContainer && itemId) {
        findAndHighlightText(elementContainer, itemId);
      }
    }

    function parseChoiceElement(element, elementId) {
      const items = [];
      const questionText = element.attributes.question || '';
      pushSanitizedItem(items, elementId + '_instructions', questionText);
      if (element.attributes.options && Array.isArray(element.attributes.options)) {
        element.attributes.options.forEach(function(option, index) {
          if (option.value && option.value.text) {
            pushSanitizedItem(items, elementId + '_' + index, option.value.text);
          }
        });
      }
      return items;
    }

    function parseDragDropImageElement(element, elementId) {
      if (dragDropImageCache[elementId]) {
        return dragDropImageCache[elementId];
      }

      const items = [];
      const text = element.attributes.text || '';
      pushSanitizedItem(items, elementId + '_instructions', text);

      const textValues = [];
      const allowsReusingChoices = element.attributes.duplicates === 'true' || element.attributes.duplicates === true;
      if (element.attributes.labels && Array.isArray(element.attributes.labels)) {
        element.attributes.labels.forEach(function(label) {
          if (label.value && label.value.text) {
            if (!allowsReusingChoices || !textValues.includes(label.value.text)) {
              textValues.push(label.value.text);
            }
          }
        });
      }
      if (element.attributes.distractors && Array.isArray(element.attributes.distractors)) {
        element.attributes.distractors.forEach(function(distractor) {
          if (distractor.value && distractor.value.text) {
            if (!allowsReusingChoices || !textValues.includes(distractor.value.text)) {
              textValues.push(distractor.value.text);
            }
          }
        });
      }

      for (let i = textValues.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = textValues[i];
        textValues[i] = textValues[j];
        textValues[j] = temp;
      }

      textValues.forEach(function(textValue) {
        pushSanitizedItem(items, elementId + '_' + textValue, textValue);
      });

      dragDropImageCache[elementId] = items;
      return items;
    }

    function parseQuestionsWithBlanks(element, elementId) {
      const items = [];
      const text = element.attributes.text || '';
      pushSanitizedItem(items, elementId + '_instructions', text);
      if (element.attributes.questions && Array.isArray(element.attributes.questions)) {
        element.attributes.questions.forEach(function(question, index) {
          if (question.value && question.value.text) {
            const questionText = question.value.text.replace(/___[^_]*___/g, 'blank');
            pushSanitizedItem(items, elementId + '_' + index, questionText);
          }
        });
      }
      return items;
    }

    function parseDragDropTextElement(element, elementId) {
      return parseQuestionsWithBlanks(element, elementId);
    }

    function parseFillInTheBlankElement(element, elementId) {
      return parseQuestionsWithBlanks(element, elementId);
    }

    function parseInlineChoiceElement(element, elementId) {
      return parseQuestionsWithBlanks(element, elementId);
    }

    function parseMultiPartElement(element, elementId) {
      const items = [];
      const text = element.attributes.text || '';
      pushSanitizedItem(items, elementId + '_instructions', text);
      if (element.attributes.questions && Array.isArray(element.attributes.questions)) {
        element.attributes.questions.forEach(function(question, questionIndex) {
          if (question.value && question.value.text) {
            pushSanitizedItem(items, elementId + '_' + questionIndex, question.value.text);
          }
          if (question.value && question.value.options && Array.isArray(question.value.options)) {
            question.value.options.forEach(function(option, optionIndex) {
              if (option.text) {
                pushSanitizedItem(items, elementId + '_' + questionIndex + '_' + optionIndex, option.text);
              }
            });
          }
        });
      }
      return items;
    }

    function parseHotTextElement(element, elementId) {
      const items = [];
      const text = element.attributes.text || '';
      pushSanitizedItem(items, elementId + '_instructions', text);
      if (element.attributes.question) {
        const questionText = element.attributes.question.replace(/___/g, '').replace(/\*/g, '');
        pushSanitizedItem(items, elementId + '_0', questionText);
      }
      return items;
    }

    function parseHotSpotElement(element, elementId) {
      const items = [];
      const text = element.attributes.text || '';
      pushSanitizedItem(items, elementId + '_instructions', text);
      if (element.attributes.labels && Array.isArray(element.attributes.labels)) {
        element.attributes.labels.forEach(function(label, index) {
          if (label.value && label.value.text && label.value.text.trim()) {
            pushSanitizedItem(items, elementId + '_' + index, label.value.text);
          }
        });
      }
      return items;
    }

    function parseElementToItems(element, elementId) {
      if (!element || !element.attributes) {
        return [];
      }
      if (element.type === 'choice' || element.type === 'quiz_choice') {
        return parseChoiceElement(element, elementId);
      } else if (element.type === 'drag_drop_image') {
        return parseDragDropImageElement(element, elementId);
      } else if (element.type === 'drag_drop_text') {
        return parseDragDropTextElement(element, elementId);
      } else if (element.type === 'fill_in_the_blank') {
        return parseFillInTheBlankElement(element, elementId);
      } else if (element.type === 'inline_choice') {
        return parseInlineChoiceElement(element, elementId);
      } else if (element.type === 'multi_part') {
        return parseMultiPartElement(element, elementId);
      } else if (element.type === 'hot_text') {
        return parseHotTextElement(element, elementId);
      } else if (element.type === 'hot_spot') {
        return parseHotSpotElement(element, elementId);
      } else {
        $log.warn('Not handled:', element.type);
        return [];
      }
    }

    function getQuestionElementItems() {
      if (ctrl.getQuestionElement) {
        const element = ctrl.getQuestionElement({ elementId: ctrl.elementId });
        if (element) {
          return parseElementToItems(element, ctrl.elementId);
        }
      }
      return [];
    }

    function getSelectedText() {
      const selection = $window.getSelection();
      if (selection && selection.toString().trim().length > 0) {
        return selection.toString().trim();
      }
      return '';
    }

    function getPlaybackItems() {
      const selectedText = getSelectedText();
      if (selectedText) {
        return [{ id: 0, text: sanitizeTextForSpeech(selectedText) }];
      }
      return ctrl.textToSpeechProps.items;
    }

    ctrl.getTextToSpeechProps = function() {
      let items = [];
      try {
        items = getQuestionElementItems() || [];
      } catch (error) {
        $log.error('Unable to prepare quiz text for speech.', error);
        items = [];
      }
      if (ctrl.textToSpeechProps.questionId !== ctrl.elementId || !angular.equals(ctrl.textToSpeechProps.items, items)) {
        ctrl.textToSpeechProps = { items: items, questionId: ctrl.elementId, getPlaybackItems: getPlaybackItems, onItemPlay: handleItemPlay, onEnd: clearHighlight };
      }
      return ctrl.textToSpeechProps;
    };
  }

})();
